import React, { useCallback, useMemo, useRef } from 'react';

import { Panel } from '@atlaskit/navigation-system/layout/panel';
import { PanelSplitter } from '@atlaskit/navigation-system/layout/panel-splitter';
import { useCurrentPreviewPanel } from '@atlassian/preview-panels-api/use-current-preview-panel';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api/use-preview-panel-actions';

import { AggRelayEnvironmentProvider } from '@townsquare/relay-utils/agg-environment';

const DEFAULT_WIDTH_PERCENTAGE = 50;
const DEFAULT_MIN_OPEN_WIDTH = 420;

type Props = {
	/**
	 * Percentage of the viewport the panel opens at and can be stretched to. The
	 * opening width is this percentage (floored at `minOpenWidth`); the maximum is the
	 * same percentage, passed to the platform Panel as a `vw` bound so the platform
	 * clamps the width live in CSS as the window resizes. Defaults to
	 * {@link DEFAULT_WIDTH_PERCENTAGE}.
	 */
	widthPercentage?: number;
	/**
	 * Minimum width, in pixels, the panel opens at. A manually-resized width is kept
	 * as-is (not re-floored) so a width dragged below this isn't bumped back up.
	 * Defaults to {@link DEFAULT_MIN_OPEN_WIDTH}.
	 */
	minOpenWidth?: number;
};

/**
 * Preview panel container with configurable sizing. Opens at, and can be stretched
 * up to, `widthPercentage` of the viewport (floored at `minOpenWidth`) - so it opens
 * at its maximum width and can only be dragged narrower. The max is a `vw` bound the
 * platform clamps live in CSS. A manually-resized width is remembered across
 * close/reopen.
 */
export const CustomWidthPreviewPanelContainer = ({
	widthPercentage = DEFAULT_WIDTH_PERCENTAGE,
	minOpenWidth = DEFAULT_MIN_OPEN_WIDTH,
}: Props): React.JSX.Element | null => {
	const panel = useCurrentPreviewPanel();
	const { setPanelWidth } = usePreviewPanelsActions();

	// Remembers the last width the user dragged to, so the panel reopens at that
	// width instead of the default. Held in a ref (not state) so resizing mid-session
	// doesn't change `defaultWidth` and remount the panel; it's read again on open.
	const lastResizedWidthRef = useRef<number | null>(null);

	const onResizeEnd = useCallback(
		({ finalWidth }: { finalWidth: number }) => {
			lastResizedWidthRef.current = finalWidth;
			setPanelWidth(finalWidth);
		},
		[setPanelWidth],
	);

	// Opening width, resolved once per open (keyed on panel id) so it stays stable
	// while the window is resized mid-session: the last dragged width if the user has
	// resized (kept as-is), otherwise `widthPercentage` of the viewport floored at
	// `minOpenWidth`.
	const defaultWidth = useMemo(
		() =>
			lastResizedWidthRef.current ??
			Math.max(
				Math.round(
					((typeof window !== 'undefined' ? window.innerWidth : 0) * widthPercentage) / 100,
				),
				minOpenWidth,
			),
		// panel id keeps the opening width stable within a browser session
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[panel?.id, widthPercentage, minOpenWidth],
	);

	if (!panel) {
		return null;
	}

	const { content: PanelContent } = panel;

	return (
		<AggRelayEnvironmentProvider>
			<Panel defaultWidth={defaultWidth} maxWidth={`${widthPercentage}vw`}>
				<PanelSplitter label="Resize preview panel" onResizeEnd={onResizeEnd} />
				<PanelContent />
			</Panel>
		</AggRelayEnvironmentProvider>
	);
};
